import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Map } from "@neshan-maps-platform/ol";
import NeshanMap, {
  NeshanMapRef,
} from "@neshan-maps-platform/react-openlayers";
import { Icon } from "../Icon";

import Feature from "@neshan-maps-platform/ol/Feature";
import Point from "@neshan-maps-platform/ol/geom/Point";
import VectorSource from "@neshan-maps-platform/ol/source/Vector";
import VectorLayer from "@neshan-maps-platform/ol/layer/Vector";
import Style from "@neshan-maps-platform/ol/style/Style";
import NeshanIcon from "@neshan-maps-platform/ol/style/Icon";
import CircleStyle from "@neshan-maps-platform/ol/style/Circle";
import Fill from "@neshan-maps-platform/ol/style/Fill";
import Stroke from "@neshan-maps-platform/ol/style/Stroke";
import { fromLonLat, toLonLat } from "@neshan-maps-platform/ol/proj";
import type { MapBrowserEvent } from "@neshan-maps-platform/ol";
import clsx from "clsx";
import {
  reverseGeocode,
  NeshanReverseResult,
} from "utils/neshan/reverseGeocode";
import { copyToClipboard } from "utils";

export type LatLng = { lat: number; lng: number };

export type CoordinateFieldsMode = "editable" | "readonly" | "hidden";

type TProps = {
  className?: string;
  onLocationPick?: (location: LatLng) => void;
  initialMarker?: LatLng;
  disabledLocationPick?: boolean;
  /**
   * Controls the lat/long coordinate fields overlay:
   * - "hidden"   (default) → no fields; behaves exactly as before.
   * - "readonly" → fields shown, display-only.
   * - "editable" → fields shown and editable; edits drive the map marker.
   */
  coordinateFields?: CoordinateFieldsMode;
  /**
   * When true, reverse-geocodes the picked location (Neshan) and floats the
   * resolved address on the map. Independent of `coordinateFields`.
   */
  showAddress?: boolean;
  /**
   * Fired (passively) whenever the map resolves an address, so a parent form can
   * decide what to do — e.g. auto-fill only when its own field is empty. Does NOT
   * force-overwrite. Also enables resolution even when `showAddress` is false.
   */
  onAddressResolved?: (address: string, raw?: NeshanReverseResult) => void;
  /**
   * Fired when the user clicks the "Use address" button in the floating card —
   * the explicit signal to overwrite the parent's address field with the
   * map-resolved address. When provided, the button is shown in the card.
   */
  onUseAddress?: (address: string, raw?: NeshanReverseResult) => void;
};

// ── Position the map view ───────────────────────────────────────────────────
// Instant, synchronous centering. Used for all programmatic positioning
// (initial marker, async coordinate updates, field edits). This does NOT rely
// on the OpenLayers animation/render loop — which is why it lands reliably in
// production, unlike the previous animated recenter that left the map stuck on
// the default location. `updateSize()` first guards against the map having been
// created at zero size inside a modal/dialog.
const positionView = (map: Map, coordinate: number[], zoom = 15) => {
  map.updateSize();
  const view = map.getView();
  view.setCenter(coordinate);
  view.setZoom(zoom);
};

// ── Animate map view to a coordinate ───────────────────────────────────────
// Animated pan, used only for the user-initiated "my location" recenter, where
// the map is already visible and sized so the animation always plays.
const animateTo = (map: Map, coordinate: number[], zoom = 15) => {
  map.getView().animate({ center: coordinate, zoom, duration: 800 });
};

export const MapLocationPicker_v2 = (props: TProps) => {
  const {
    className,
    onLocationPick,
    initialMarker,
    disabledLocationPick,
    coordinateFields = "hidden",
    showAddress = false,
    onAddressResolved,
    onUseAddress,
  } = props;

  const { t } = useTranslation();

  const mapRef = useRef<NeshanMapRef | null>(null);
  const disabledPickRef = useRef(disabledLocationPick ?? false);
  const pickSourceRef = useRef<VectorSource | null>(null);
  const userLayerRef = useRef<VectorLayer<any> | null>(null);
  const staticLayerRef = useRef<VectorLayer<any> | null>(null);
  const userCoordRef = useRef<number[] | null>(null); // cached GPS coordinate

  // The OL map instance, published to state in onInit. Keeping it in state (not
  // a ref) gives effects a clean "map is ready" dependency, so centering runs
  // deterministically once the map exists — regardless of whether the marker
  // was available before or after the map finished initializing.
  const [map, setMap] = useState<Map | null>(null);

  // ── Coordinate fields state ──────────────────────────────────────────────
  const fieldsModeRef = useRef(coordinateFields);
  const onLocationPickRef = useRef(onLocationPick);
  const editDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [latText, setLatText] = useState(
    initialMarker ? String(initialMarker.lat) : ""
  );
  const [lngText, setLngText] = useState(
    initialMarker ? String(initialMarker.lng) : ""
  );

  // ── Address (reverse-geocoding) state ────────────────────────────────────
  const showAddressRef = useRef(showAddress);
  const onAddressResolvedRef = useRef(onAddressResolved);
  const onUseAddressRef = useRef(onUseAddress);
  const addrDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addrAbortRef = useRef<AbortController | null>(null);
  const addrRequestIdRef = useRef(0);
  const addrRawRef = useRef<NeshanReverseResult | null>(null);
  const [address, setAddress] = useState("");
  const [addrExpanded, setAddrExpanded] = useState(true);

  // Reverse-geocode the picked coordinate (debounced, latest-wins). No-op unless
  // the address is being shown or a consumer wants it via callback.
  const resolveAddress = (lat: number, lng: number) => {
    if (
      !showAddressRef.current &&
      !onAddressResolvedRef.current &&
      !onUseAddressRef.current
    )
      return;

    if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
    addrDebounceRef.current = setTimeout(() => {
      addrAbortRef.current?.abort();
      const controller = new AbortController();
      addrAbortRef.current = controller;
      const requestId = ++addrRequestIdRef.current;

      reverseGeocode(lat, lng, controller.signal).then((result) => {
        // Ignore stale responses (a newer pick superseded this one).
        if (requestId !== addrRequestIdRef.current) return;
        addrRawRef.current = result;
        const resolved = result?.formatted_address ?? "";
        setAddress(resolved);
        if (result) {
          // Passive notification — the consumer decides whether to fill.
          onAddressResolvedRef.current?.(resolved, result);
        }
      });
    }, 300);
  };

  // Stable initial center so re-renders (from field state) don't yank the map.
  const initialCenter = useMemo(
    () =>
      initialMarker
        ? { latitude: initialMarker.lat, longitude: initialMarker.lng }
        : { latitude: 35.705803, longitude: 51.4217049 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialMarker?.lat, initialMarker?.lng]
  );

  // ── "You are here" dot + pulse ring ──────────────────────────────────────
  const placeUserMarker = (map: Map, coordinate: number[]) => {
    // Remove stale user layer if it exists
    if (userLayerRef.current) {
      map.removeLayer(userLayerRef.current);
    }

    const userDot = new Feature({ geometry: new Point(coordinate) });
    userDot.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#14304D" }),
          stroke: new Stroke({ color: "#fff", width: 2.5 }),
        }),
      })
    );

    const pulseRing = new Feature({ geometry: new Point(coordinate) });
    pulseRing.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 16,
          fill: new Fill({ color: "rgba(20,48,77,0.15)" }),
          stroke: new Stroke({ color: "rgba(20,48,77,0.4)", width: 1.5 }),
        }),
      })
    );

    const layer = new VectorLayer({
      source: new VectorSource({ features: [pulseRing, userDot] }),
      zIndex: 50,
    });

    userLayerRef.current = layer;
    map.addLayer(layer);
  };

  // ── Geolocation → center + marker ────────────────────────────────────────
  const centerOnUserLocation = (map: Map) => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const coordinate = fromLonLat([coords.longitude, coords.latitude]);
        userCoordRef.current = coordinate;
        animateTo(map, coordinate);
        placeUserMarker(map, coordinate);
      },
      (err) => console.warn("Geolocation error:", err.message),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  // ── Re-center using cached coord (instant) or re-fetch GPS ───────────────
  const handleRecenter = () => {
    if (!map) return;

    if (userCoordRef.current) {
      animateTo(map, userCoordRef.current);
    } else {
      centerOnUserLocation(map);
    }
  };

  // ── Static asset marker ───────────────────────────────────────────────────
  const addStaticMarker = (map: Map, latLng: LatLng) => {
    if (staticLayerRef.current) map.removeLayer(staticLayerRef.current);

    const marker = new Feature({
      geometry: new Point(fromLonLat([latLng.lng, latLng.lat])),
    });
    marker.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#14304D" }),
          stroke: new Stroke({ color: "#fff", width: 2.5 }),
        }),
        // image: new NeshanIcon({
        //   src: "/waterMeterMapMarker.png",
        //   scale: 0.08,
        //   anchor: [0.5, 1],
        // }),
      })
    );

    const layer = new VectorLayer({
      source: new VectorSource({ features: [marker] }),
    });
    staticLayerRef.current = layer;
    map.addLayer(layer);
  };

  // ── Click-to-pick marker ──────────────────────────────────────────────────
  const placePickMarker = (map: Map, coordinate: number[]) => {
    if (!pickSourceRef.current) {
      const source = new VectorSource();
      pickSourceRef.current = source;
      map.addLayer(new VectorLayer({ source, zIndex: 100 }));
    }
    pickSourceRef.current.clear();

    const pin = new Feature({ geometry: new Point(coordinate) });
    pin.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: "#F58220" }),
          stroke: new Stroke({ color: "#fff", width: 2.5 }),
        }),
      })
    );
    pickSourceRef.current.addFeature(pin);
  };

  const registerClickHandler = (map: Map) => {
    map.on("click", (event: MapBrowserEvent<MouseEvent>) => {
      if (disabledPickRef.current) return;
      const [lng, lat] = toLonLat(event.coordinate);
      placePickMarker(map, event.coordinate);
      onLocationPickRef.current?.({ lat, lng });
      // Keep the coordinate fields in sync with map clicks.
      if (fieldsModeRef.current !== "hidden") {
        setLatText(String(lat));
        setLngText(String(lng));
      }
      resolveAddress(lat, lng);
    });
  };

  // ── Apply edited coordinate text → map marker (debounced) ─────────────────
  const applyCoordsToMap = (latRaw: string, lngRaw: string) => {
    if (!map) return;
    if (latRaw.trim() === "" || lngRaw.trim() === "") return;

    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    const valid =
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;
    if (!valid) return;

    const coordinate = fromLonLat([lng, lat]);
    placePickMarker(map, coordinate);
    animateTo(map, coordinate);
    // Once the user drives the point via fields, drop the static initialMarker
    // marker so a single (red) point represents the selection.
    if (staticLayerRef.current) {
      map.removeLayer(staticLayerRef.current);
      staticLayerRef.current = null;
    }
    onLocationPickRef.current?.({ lat, lng });
    resolveAddress(lat, lng);
  };

  const handleLatChange = (value: string) => {
    setLatText(value);
    if (editDebounceRef.current) clearTimeout(editDebounceRef.current);
    editDebounceRef.current = setTimeout(
      () => applyCoordsToMap(value, lngText),
      300
    );
  };

  const handleLngChange = (value: string) => {
    setLngText(value);
    if (editDebounceRef.current) clearTimeout(editDebounceRef.current);
    editDebounceRef.current = setTimeout(
      () => applyCoordsToMap(latText, value),
      300
    );
  };

  // Both coordinates must parse as finite numbers before copying is offered.
  const canCopyCoords =
    Number.isFinite(Number(latText)) &&
    Number.isFinite(Number(lngText)) &&
    latText.trim() !== "" &&
    lngText.trim() !== "";

  // Copy as "lat, lng" — the format map services accept when pasted.
  const handleCopyCoords = () => {
    if (!canCopyCoords) return;
    copyToClipboard(
      `${latText.trim()}, ${lngText.trim()}`,
      t("coordinatesCopied")
    );
  };

  // Show the static marker and center the view on it (instant positioning).
  const centerOnMarker = (map: Map, marker: LatLng) => {
    addStaticMarker(map, marker);
    positionView(map, fromLonLat([marker.lng, marker.lat]));
    // Reflect externally-driven marker updates in the coordinate fields.
    if (fieldsModeRef.current !== "hidden") {
      setLatText(String(marker.lat));
      setLngText(String(marker.lng));
    }
    resolveAddress(marker.lat, marker.lng);
  };

  const onInit = (map: Map) => {
    map.setMapType("neshan");
    registerClickHandler(map);
    // Publish the ready map so handlers and the async effect can use it.
    setMap(map);

    // Center right away: on the initial marker if we have one, otherwise on the
    // user's location. This is the first-paint position; async marker updates
    // are handled by the effect below.
    if (initialMarker) {
      centerOnMarker(map, initialMarker);
    } else {
      centerOnUserLocation(map);
    }
  };

  useEffect(() => {
    disabledPickRef.current = disabledLocationPick ?? false;
  }, [disabledLocationPick]);

  useEffect(() => {
    fieldsModeRef.current = coordinateFields;
  }, [coordinateFields]);

  useEffect(() => {
    onLocationPickRef.current = onLocationPick;
  }, [onLocationPick]);

  useEffect(() => {
    showAddressRef.current = showAddress;
  }, [showAddress]);

  useEffect(() => {
    onAddressResolvedRef.current = onAddressResolved;
  }, [onAddressResolved]);

  useEffect(() => {
    onUseAddressRef.current = onUseAddress;
  }, [onUseAddress]);

  // Clear any pending debounced edit / in-flight geocode on unmount.
  useEffect(() => {
    return () => {
      if (editDebounceRef.current) clearTimeout(editDebounceRef.current);
      if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
      addrAbortRef.current?.abort();
    };
  }, []);

  // Re-center when the coordinates arrive/change after the map is ready — the
  // async case (apartment edit/details load their location after mount) that
  // onInit alone can't cover. `map` in the deps also covers the reverse order
  // (marker present before the map finished initializing). Positioning is
  // synchronous (positionView), so it lands in production instead of being
  // dropped by the animation loop, which was the root cause of the map staying
  // on the default location. No geolocation branch here — that is triggered
  // once in onInit — so re-renders never re-prompt for location.
  useEffect(() => {
    if (!map || !initialMarker) return;
    centerOnMarker(map, initialMarker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, initialMarker?.lat, initialMarker?.lng]);

  return (
    // Wrapper must be relative so the FAB is positioned inside the map bounds
    <div className={clsx("relative w-full h-full", className)}>
      <NeshanMap
        className="w-full h-full"
        ref={mapRef}
        defaultType="dreamy"
        mapKey="web.13887c11a0624be0b9ceb86184273df9"
        zoom={14}
        center={initialCenter}
        onInit={onInit}
        poi={false}
        traffic={false}
      />

      {/* ── Coordinate fields overlay (top-left) ─────────────────────────── */}
      {coordinateFields !== "hidden" && (
        <div
          className="
            absolute top-3 left-3 z-50
            flex flex-col gap-2
            bg-white shadow-lg rounded-lg
            border border-neutral-200
            p-3
          "
        >
          {/* Header: copy both coordinates as "lat, lng" */}
          <div className="absolute flex items-center justify-end top-1 left-1">
            <button
              type="button"
              onClick={handleCopyCoords}
              disabled={!canCopyCoords}
              title={t("copyCoordinates")}
              aria-label={t("copyCoordinates")}
              className={clsx(
                "group flex items-center justify-center rounded-md p-1 transition-all",
                canCopyCoords
                  ? "hover:bg-neutral-100 active:scale-95 cursor-pointer"
                  : "cursor-not-allowed"
              )}
            >
              <Icon
                icon={"MdCopyAll"}
                className={clsx(
                  "text-base",
                  canCopyCoords
                    ? "text-gray-40 group-hover:text-blue-80"
                    : "text-neutral-300"
                )}
              />
            </button>
          </div>

          <label className="flex flex-col gap-1 text-xs text-gray-700">
            <span>{t("latitude")}</span>
            <input
              type="text"
              inputMode="decimal"
              dir="ltr"
              value={latText}
              readOnly={coordinateFields === "readonly"}
              onChange={(e) => handleLatChange(e.target.value)}
              className={clsx(
                "w-40 rounded-md border border-neutral-300 px-2 py-1 text-sm outline-none",
                coordinateFields === "readonly"
                  ? "bg-neutral-100 text-gray-500 cursor-default"
                  : "bg-white focus:border-blue-500"
              )}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-700">
            <span>{t("longitude")}</span>
            <input
              type="text"
              inputMode="decimal"
              dir="ltr"
              value={lngText}
              readOnly={coordinateFields === "readonly"}
              onChange={(e) => handleLngChange(e.target.value)}
              className={clsx(
                "w-40 rounded-md border border-neutral-300 px-2 py-1 text-sm outline-none",
                coordinateFields === "readonly"
                  ? "bg-neutral-100 text-gray-500 cursor-default"
                  : "bg-white focus:border-blue-500"
              )}
            />
          </label>
        </div>
      )}

      {/* ── Resolved address card (bottom-center) — only once resolved ────── */}
      {showAddress && address && (
        <div
          dir="rtl"
          className="
            absolute bottom-6 left-1/2 -translate-x-1/2 z-50
            max-w-[calc(100%-6rem)]
            flex flex-col gap-2
            bg-white shadow-lg rounded-lg
            border border-neutral-200
            p-3
          "
        >
          {/* Header: label + expand/collapse toggle */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-700">{t("address")}</span>
            <button
              type="button"
              onClick={() => setAddrExpanded((v) => !v)}
              aria-label={addrExpanded ? "collapse address" : "expand address"}
              className="text-gray-500 hover:text-gray-700 active:scale-95 transition-all"
            >
              <Icon icon={addrExpanded ? "MdExpandMore" : "MdExpandLess"} />
            </button>
          </div>

          {addrExpanded && (
            <>
              <p className="text-sm text-gray-900 break-words leading-6">
                {address}
              </p>
              {onUseAddress && (
                <button
                  type="button"
                  onClick={() =>
                    onUseAddressRef.current?.(
                      address,
                      addrRawRef.current ?? undefined
                    )
                  }
                  className="
                    self-start text-xs rounded-md px-3 py-1.5
                    bg-blue-600 text-white
                    hover:bg-blue-700 active:scale-95
                    transition-all duration-150
                  "
                >
                  {t("useThisAddress")}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Recenter FAB ─────────────────────────────────────────────────── */}
      <button
        onClick={handleRecenter}
        aria-label="Center map on my location"
        className="
          absolute bottom-6 right-4 z-50
          w-11 h-11 rounded-full
          bg-white shadow-lg
          flex items-center justify-center
          text-blue-600
          hover:bg-blue-50 active:scale-95
          transition-all duration-150
          border border-neutral-200
        "
      >
        {/* GPS / crosshair icon (inline SVG — no extra dep needed) */}
        <Icon icon={"MdMyLocation"} />
      </button>
    </div>
  );
};
